import { Typography, useTheme } from "@mui/material";
import { Box, SxProps, Theme } from "@mui/system";
import type { JSX } from "react";
import { useEffect, useMemo, useRef } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { InfoIcon } from "@/assets";
import { FormControlSelect } from "@/components/formControls/formControlSelect/FormControlSelect";
import { FormControlToggleButtonGroup, ToggleOption } from "@/components/formControls/formControlToggleButtonGroup";
import {
  BathroomEnum,
  BuildingStandardDeterminationMethodEnum,
  BuildingTypeEnum,
  FloorCoveringEnum,
  HeatingEnum,
  ObjectTypeEnum,
  ResidentialAreaEnum,
  RoofCoveringEnum,
  StoreRoomEnum,
  SurfaceMountedInstallationEnum,
  ValueEnum,
  WindowsEnum,
} from "@/types/refineRealEstate";
import { PropertyInformation } from "@/types/refineRealEstate/refineRealEstate";

import { StyledFormGridContainer, StyledFormSection, StyledFormSectionTitle } from "../refineRealEstate.styled";
import {
  defaultEquipmentIndicators,
  defaultEquipmentIndicatorsApartment,
  defaultEquipmentIndicatorsHouse,
  defaultEquipmentIndicatorsMultifamilyHouse,
} from "./defaultModernizationEquipmentMatrix";

const toggleButtonGroupStyle: SxProps<Theme> = {
  gap: "4px",
  display: "grid",
  gridTemplateColumns: "repeat(4, 1fr)",
};

interface BuildingDataTabProps {
  readonly?: boolean;
}

type EquipmentValueOption = ToggleOption<ValueEnum> & { description: string };

export const BuildingStandardTab = ({ readonly }: BuildingDataTabProps): JSX.Element => {
  const { t } = useTranslation("Common", { keyPrefix: "realEstate" });
  const theme = useTheme();

  const { control, watch, setValue } = useFormContext<PropertyInformation>();

  const { generalInfo, buildingStandard } = watch();

  type DeterminationMethodTranslationKeys =
    | "buildingStandardDeterminationMethod.values.detailed"
    | "buildingStandardDeterminationMethod.values.automatic"
    | "buildingStandardDeterminationMethod.values.automatic.description"
    | "buildingStandardDeterminationMethod.values.simple";

  const determinationMethodOptions = Object.entries(BuildingStandardDeterminationMethodEnum).map(([key, value]) => ({
    value,
    label: t(`buildingStandardDeterminationMethod.values.${key}` as DeterminationMethodTranslationKeys),
  }));

  // Reusable yes/no options
  const yesNoOptions = useMemo(
    () => [
      { value: true, label: t("yes"), disabled: !!readonly },
      { value: false, label: t("no"), disabled: !!readonly },
    ],
    [t, readonly],
  );

  const bathroomYesNoOptions: ToggleOption<BathroomEnum>[] = useMemo(
    () => [
      { value: BathroomEnum.moreThanOneBath, label: t("yes"), disabled: !!readonly },
      { value: BathroomEnum.oneBath, label: t("no"), disabled: !!readonly },
    ],
    [t, readonly],
  );

  const bathRoomOptions: ToggleOption<BathroomEnum>[] = useMemo(
    () => [
      { value: BathroomEnum.inside, label: t("bathRoom.internal"), disabled: !!readonly },
      { value: BathroomEnum.withWindow, label: t("bathRoom.withWindow"), disabled: !!readonly },
      { value: BathroomEnum.moreThanOneBath, label: t("bathRoom.moreThanOneBath"), disabled: !!readonly },
    ],
    [t, readonly],
  );

  const surfaceMountedInstallationOptions: ToggleOption<SurfaceMountedInstallationEnum>[] = useMemo(
    () => [
      { value: SurfaceMountedInstallationEnum.cablesOnWalls, label: t("yes"), disabled: !!readonly },
      { value: SurfaceMountedInstallationEnum.cablesNotOnWalls, label: t("no"), disabled: !!readonly },
    ],
    [t, readonly],
  );

  const heatingOptions: ToggleOption<HeatingEnum>[] = useMemo(
    () => [
      { value: HeatingEnum.stovesHeating, label: t("heating.stovesHeating"), disabled: !!readonly },
      { value: HeatingEnum.floorHeating, label: t("heating.floorHeating"), disabled: !!readonly },
      { value: HeatingEnum.centralHeating, label: t("heating.centralHeating"), disabled: !!readonly },
      { value: HeatingEnum.other, label: t("heating.other"), disabled: !!readonly },
    ],
    [t, readonly],
  );

  const isHeatingSystemDisabled =
    buildingStandard.matrix?.heating === HeatingEnum.stovesHeating ||
    buildingStandard.matrix?.heating === HeatingEnum.other;

  const heatingSystemOptions: ToggleOption<boolean>[] = useMemo(
    () => [
      { value: true, label: t("yes"), disabled: isHeatingSystemDisabled || !!readonly },
      { value: false, label: t("no"), disabled: isHeatingSystemDisabled || !!readonly },
    ],
    [isHeatingSystemDisabled, t, readonly],
  );

  const residentialAreaOptions: ToggleOption<ResidentialAreaEnum>[] = useMemo(
    () => [
      { value: ResidentialAreaEnum.noBalcony, label: t("outdoorLivingAreas.noBalcony"), disabled: !!readonly },
      {
        value: ResidentialAreaEnum.balconyUnder10,
        label: t("outdoorLivingAreas.balconyUnder10"),
        disabled: !!readonly,
      },
      {
        value: ResidentialAreaEnum.balconyAbove10,
        label: t("outdoorLivingAreas.balconyAbove10"),
        disabled: !!readonly,
      },
      { value: ResidentialAreaEnum.winterGarden, label: t("outdoorLivingAreas.winterGarden"), disabled: !!readonly },
    ],
    [t, readonly],
  );

  const roofCoveringOptions: ToggleOption<RoofCoveringEnum>[] = useMemo(
    () => [
      { value: RoofCoveringEnum.roofingFelt, label: t("roofing.roofingFelt"), disabled: !!readonly },
      { value: RoofCoveringEnum.roofingTiles, label: t("roofing.roofingTiles"), disabled: !!readonly },
      { value: RoofCoveringEnum.slate, label: t("roofing.slate"), disabled: !!readonly },
      { value: RoofCoveringEnum.metal, label: t("roofing.metal"), disabled: !!readonly },
      { value: RoofCoveringEnum.other, label: t("roofing.other"), disabled: !!readonly },
    ],
    [t, readonly],
  );

  const floorOptions: ToggleOption<FloorCoveringEnum>[] = useMemo(
    () => [
      { value: FloorCoveringEnum.plasticPVC, label: t("floor.plasticPVC"), disabled: !!readonly },
      { value: FloorCoveringEnum.tiles, label: t("floor.tiles"), disabled: !!readonly },
      { value: FloorCoveringEnum.parquetNaturalStone, label: t("floor.parquetNaturalStone"), disabled: !!readonly },
      { value: FloorCoveringEnum.carpetLaminate, label: t("floor.carpetLaminate"), disabled: !!readonly },
      { value: FloorCoveringEnum.other, label: t("floor.other"), disabled: !!readonly },
    ],
    [t, readonly],
  );

  const windowsOptions: ToggleOption<WindowsEnum>[] = useMemo(
    () => [
      { value: WindowsEnum.simple, label: t("windows.simple"), disabled: !!readonly },
      { value: WindowsEnum.doubleGlazing, label: t("windows.doubleGlazing"), disabled: !!readonly },
      { value: WindowsEnum.tripleGlazing, label: t("windows.tripleGlazing"), disabled: !!readonly },
      { value: WindowsEnum.boxWindow, label: t("windows.boxWindow"), disabled: !!readonly },
      { value: WindowsEnum.floorToCeilingWindow, label: t("windows.floorToCeilingWindow"), disabled: !!readonly },
      { value: WindowsEnum.specialGlazing, label: t("windows.specialGlazing"), disabled: !!readonly },
      { value: WindowsEnum.other, label: t("windows.other"), disabled: !!readonly },
    ],
    [t, readonly],
  );

  const storageRoomOptions: ToggleOption<StoreRoomEnum>[] = useMemo(
    () => [
      { value: StoreRoomEnum.none, label: t("storeRoom.no"), disabled: !!readonly },
      { value: StoreRoomEnum.inside, label: t("storeRoom.inside"), disabled: !!readonly },
      { value: StoreRoomEnum.outside, label: t("storeRoom.outside"), disabled: !!readonly },
      { value: StoreRoomEnum.outsideAndInside, label: t("storeRoom.outsideAndInside"), disabled: !!readonly },
    ],
    [t, readonly],
  );

  const storageRoomYesNoOptions: ToggleOption<StoreRoomEnum>[] = useMemo(
    () => [
      { value: StoreRoomEnum.outsideAndInside, label: t("yes"), disabled: !!readonly },
      { value: StoreRoomEnum.none, label: t("no"), disabled: !!readonly },
    ],
    [t, readonly],
  );

  const levelOptions: EquipmentValueOption[] = useMemo(
    () => [
      {
        value: ValueEnum.verySimple,
        label: t("equipmentValue.verySimple"),
        description: t("equipmentValue.verySimple.description"),
        disabled: !!readonly,
      },
      {
        value: ValueEnum.simple,
        label: t("equipmentValue.simple"),
        description: t("equipmentValue.simple.description"),
        disabled: !!readonly,
      },
      {
        value: ValueEnum.simpleToMedium,
        label: t("equipmentValue.simpleToMedium"),
        description: t("equipmentValue.simpleToMedium.description"),
        disabled: !!readonly,
      },
      {
        value: ValueEnum.medium,
        label: t("equipmentValue.medium"),
        description: t("equipmentValue.medium.description"),
        disabled: !!readonly,
      },
      {
        value: ValueEnum.mediumToHigh,
        label: t("equipmentValue.mediumToHigh"),
        description: t("equipmentValue.mediumToHigh.description"),
        disabled: !!readonly,
      },
      {
        value: ValueEnum.high,
        label: t("equipmentValue.high"),
        description: t("equipmentValue.high.description"),
        disabled: !!readonly,
      },
      {
        value: ValueEnum.highToVeryHigh,
        label: t("equipmentValue.highToVeryHigh"),
        description: t("equipmentValue.highToVeryHigh.description"),
        disabled: !!readonly,
      },
      {
        value: ValueEnum.veryHigh,
        label: t("equipmentValue.veryHigh"),
        description: t("equipmentValue.veryHigh.description"),
        disabled: !!readonly,
      },
      {
        value: ValueEnum.veryVeryHigh,
        label: t("equipmentValue.veryVeryHigh"),
        description: t("equipmentValue.veryVeryHigh.description"),
        disabled: !!readonly,
      },
    ],
    [t, readonly],
  );

  const selectedEquipmentValueOption = levelOptions.find((option) => option.value === buildingStandard.value);
  const selectedEquipmentValueDescription = selectedEquipmentValueOption?.description;
  const selectedEquipmentValueLabel = selectedEquipmentValueOption?.label;
  const selectedEquipmentValueLabelCapitalized =
    typeof selectedEquipmentValueLabel === "string"
      ? selectedEquipmentValueLabel.charAt(0).toUpperCase() + selectedEquipmentValueLabel.slice(1)
      : undefined;

  const isHouse = generalInfo.objectType === ObjectTypeEnum.house;
  const isApartment = generalInfo.buildingType === BuildingTypeEnum.apartment;
  const isMultiFamilyHouse = generalInfo.buildingType === BuildingTypeEnum.multiFamilyHouse;
  const isMixedUseProperty = generalInfo.buildingType === BuildingTypeEnum.mixedUseProperty;

  const prevDeterminationMethod = useRef<BuildingStandardDeterminationMethodEnum | undefined>(undefined);

  useEffect(() => {
    if (
      buildingStandard.method === BuildingStandardDeterminationMethodEnum.simple &&
      prevDeterminationMethod.current !== buildingStandard.method &&
      prevDeterminationMethod.current !== undefined
    ) {
      setValue("buildingStandard.value", ValueEnum.simple);
    } else if (
      buildingStandard.method === BuildingStandardDeterminationMethodEnum.detailed &&
      prevDeterminationMethod.current !== buildingStandard.method &&
      prevDeterminationMethod.current !== undefined
    ) {
      if (isHouse)
        setValue("buildingStandard.matrix", {
          ...defaultEquipmentIndicators,
          ...defaultEquipmentIndicatorsHouse,
        });
      if (isApartment) {
        setValue("buildingStandard.matrix", {
          ...defaultEquipmentIndicators,
          ...defaultEquipmentIndicatorsApartment,
        });
      }
      if (isMultiFamilyHouse || isMixedUseProperty)
        setValue("buildingStandard.matrix", {
          ...defaultEquipmentIndicators,
          ...defaultEquipmentIndicatorsMultifamilyHouse,
        });
    }
    prevDeterminationMethod.current = buildingStandard.method;
  }, [buildingStandard.method, setValue, isHouse, isApartment, isMultiFamilyHouse, isMixedUseProperty]);

  useEffect(() => {
    if (
      buildingStandard.matrix?.heating === HeatingEnum.stovesHeating ||
      buildingStandard.matrix?.heating === HeatingEnum.other
    ) {
      setValue("buildingStandard.matrix.heatingSystem", false);
    }
  }, [buildingStandard.matrix?.heating, setValue]);

  return (
    <StyledFormGridContainer>
      <StyledFormSection>
        <StyledFormSectionTitle>{t("buildingStandardDeterminationMethod.title")}</StyledFormSectionTitle>
        <FormControlSelect
          name="buildingStandard.method"
          control={control}
          options={determinationMethodOptions}
          fullWidth
          selectSx={{ height: "45px" }}
          disabled={!!readonly}
          data-cy="refine-data-equipment-method"
        />
      </StyledFormSection>
      {/* Automatic method */}
      {buildingStandard.method === BuildingStandardDeterminationMethodEnum.automatic && (
        <Box
          sx={{ gridColumn: "span 2", display: "flex", alignItems: "center", gap: "8px" }}
          data-cy="refine-data-equipment-automatic-description"
        >
          <InfoIcon sx={{ color: "primary.main", fontSize: theme.analysisLayout.refinementInfoIconSize }} />
          <Typography>{t("buildingStandardDeterminationMethod.values.automatic.description")}</Typography>
        </Box>
      )}
      {/* Indicators method */}
      {buildingStandard.method === BuildingStandardDeterminationMethodEnum.detailed && (
        <>
          {isHouse && (
            <Box sx={{ gridColumn: "span 2" }}>
              <FormControlToggleButtonGroup
                label={t("bathRoom.moreThanOneBath").charAt(0).toUpperCase() + t("bathRoom.moreThanOneBath").slice(1)}
                control={control}
                name="buildingStandard.matrix.bathroom"
                fullWidth
                sx={toggleButtonGroupStyle}
                options={bathroomYesNoOptions}
                data-cy="refine-data-equipment-matrix-bathroom"
              />
            </Box>
          )}
          {isApartment && (
            <Box sx={{ gridColumn: "span 2" }}>
              <FormControlToggleButtonGroup
                label={t("bathRoom.title")}
                control={control}
                name="buildingStandard.matrix.bathroom"
                fullWidth
                sx={toggleButtonGroupStyle}
                options={bathRoomOptions}
                data-cy="refine-data-equipment-matrix-bathroom"
              />
            </Box>
          )}
          <Box sx={{ gridColumn: "span 2" }}>
            <FormControlToggleButtonGroup
              label={t("separateWC")}
              control={control}
              name="buildingStandard.matrix.guestToilet"
              fullWidth
              sx={toggleButtonGroupStyle}
              options={yesNoOptions}
              data-cy="refine-data-equipment-guest-toilet"
            />
          </Box>
          {isHouse && (
            <>
              <Box sx={{ gridColumn: "span 2" }}>
                <FormControlToggleButtonGroup
                  label={t("wellnessFeatures")}
                  control={control}
                  name="buildingStandard.matrix.sauna"
                  fullWidth
                  sx={toggleButtonGroupStyle}
                  options={yesNoOptions}
                  data-cy="refine-data-equipment-matrix-sauna"
                />
              </Box>
              <Box sx={{ gridColumn: "span 2" }}>
                <FormControlToggleButtonGroup
                  label={t("wiringSurfaceMounted")}
                  control={control}
                  name="buildingStandard.matrix.surfaceMountedInstallation"
                  fullWidth
                  sx={toggleButtonGroupStyle}
                  options={surfaceMountedInstallationOptions}
                  data-cy="refine-data-equipment-matrix-surface-mounted-installation"
                />
              </Box>
            </>
          )}
          <Box sx={{ gridColumn: "span 2" }}>
            <FormControlToggleButtonGroup
              label={t("exteriorWallsInsulated")}
              control={control}
              name="buildingStandard.matrix.insulatedExteriorWalls"
              fullWidth
              sx={toggleButtonGroupStyle}
              options={yesNoOptions}
              data-cy="refine-data-equipment-insulated-exterior-walls"
            />
          </Box>
          {isHouse && (
            <Box sx={{ gridColumn: "span 2" }}>
              <FormControlToggleButtonGroup
                label={t("fireplaceSystemSolarThermalEnergy")}
                control={control}
                name="buildingStandard.matrix.fireplace"
                fullWidth
                sx={toggleButtonGroupStyle}
                options={yesNoOptions}
                data-cy="refine-data-equipment-matrix-fireplace"
              />
            </Box>
          )}

          <Box sx={{ gridColumn: "span 2" }}>
            <FormControlToggleButtonGroup
              label={t("heating.title")}
              control={control}
              name="buildingStandard.matrix.heating"
              fullWidth
              sx={toggleButtonGroupStyle}
              options={heatingOptions}
              data-cy="refine-data-equipment-matrix-heating"
            />
          </Box>

          <Box sx={{ gridColumn: "span 2", opacity: isHeatingSystemDisabled ? 0.5 : 1 }}>
            <FormControlToggleButtonGroup
              label={t("viaHeatPumpOrCombined")}
              control={control}
              name="buildingStandard.matrix.heatingSystem"
              fullWidth
              sx={toggleButtonGroupStyle}
              options={heatingSystemOptions}
              data-cy="refine-data-equipment-matrix-heating-system"
            />
          </Box>

          {(isApartment || isMultiFamilyHouse || isMixedUseProperty) && (
            <Box sx={{ gridColumn: "span 2" }}>
              <FormControlToggleButtonGroup
                label={t("outdoorLivingAreas.title")}
                control={control}
                name="buildingStandard.matrix.residentialArea"
                fullWidth
                sx={toggleButtonGroupStyle}
                options={
                  isMultiFamilyHouse || isMixedUseProperty
                    ? residentialAreaOptions.filter((option) => option.value !== ResidentialAreaEnum.winterGarden)
                    : residentialAreaOptions
                }
                data-cy="refine-data-equipment-matrix-residential-area"
              />
            </Box>
          )}
          {(isHouse || isMultiFamilyHouse || isMixedUseProperty) && (
            <Box sx={{ gridColumn: "span 2" }}>
              <FormControlToggleButtonGroup
                label={t("roofing.title")}
                control={control}
                name="buildingStandard.matrix.roofCovering"
                fullWidth
                sx={toggleButtonGroupStyle}
                options={roofCoveringOptions}
                data-cy="refine-data-equipment-matrix-roof-covering"
              />
            </Box>
          )}
          {isApartment && (
            <Box sx={{ gridColumn: "span 2" }}>
              <FormControlToggleButtonGroup
                label={t("floor.title")}
                control={control}
                name="buildingStandard.matrix.floorCovering"
                fullWidth
                sx={toggleButtonGroupStyle}
                options={floorOptions}
                data-cy="refine-data-equipment-matrix-floor-covering"
              />
            </Box>
          )}
          <Box sx={{ gridColumn: "span 2" }}>
            <FormControlToggleButtonGroup
              label={t("windows.title")}
              control={control}
              name="buildingStandard.matrix.windows"
              fullWidth
              sx={toggleButtonGroupStyle}
              options={windowsOptions}
              data-cy="refine-data-equipment-matrix-windows"
            />
          </Box>
          {(isApartment || isMultiFamilyHouse || isMixedUseProperty) && (
            <Box sx={{ gridColumn: "span 2" }}>
              <FormControlToggleButtonGroup
                label={t("storeRoom.title")}
                control={control}
                name="buildingStandard.matrix.storeRoom"
                fullWidth
                sx={toggleButtonGroupStyle}
                options={isMultiFamilyHouse || isMixedUseProperty ? storageRoomYesNoOptions : storageRoomOptions}
                data-cy="refine-data-equipment-matrix-store-room"
              />
            </Box>
          )}
        </>
      )}
      {/* classified method */}
      {buildingStandard.method === BuildingStandardDeterminationMethodEnum.simple && (
        <Box sx={{ gridColumn: "span 2", pt: "30px" }}>
          <FormControlToggleButtonGroup
            control={control}
            name="buildingStandard.value"
            fullWidth
            sx={toggleButtonGroupStyle}
            options={levelOptions}
            data-cy="refine-data-equipment-value"
          />
          {selectedEquipmentValueDescription && (
            <Typography sx={{ mt: "22px" }}>
              <Box component="span" sx={{ fontWeight: 600 }}>
                {selectedEquipmentValueLabelCapitalized}:
              </Box>{" "}
              {selectedEquipmentValueDescription}
            </Typography>
          )}
        </Box>
      )}
    </StyledFormGridContainer>
  );
};
